import {FC, useEffect, useState} from 'react';
import {useOktaAuth} from '@okta/okta-react';
import {useTranslation} from 'react-i18next';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';

interface StudentInfo {
    studentName: string;
    studentId: string;
    submittedTimestamp?: string | null;
}

interface AssessmentHistoryRecord {
    assessmentId: string;
    assessmentName: string;
    enrolledStudents: StudentInfo[];
}

interface AssessmentHistoryProps {
    DialogOpen: boolean;
    handleDialogClose: () => void;
    classId?: string;
    courseId?: string;
}

export const ClassAssessmentHistory: FC<AssessmentHistoryProps> = ({
    DialogOpen,
    handleDialogClose,
    classId
}) => {
    const {authState} = useOktaAuth();
    const {t} = useTranslation();
    const [records, setRecords] = useState<AssessmentHistoryRecord[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!classId || !DialogOpen) {
            return;
        }

        setIsLoading(true);
        setRecords([]);

        fetch(`${process.env.REACT_APP_BACKEND_URL}/assessmenthistory?classId=${encodeURIComponent(classId)}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        })
            .then((res) => res.json())
            .then((data: AssessmentHistoryRecord[]) => {
                if (Array.isArray(data)) {
                    setRecords(data);
                }
                setIsLoading(false);
                return data;
            })
            .catch((error) => {
                console.error('Error fetching assessment history:', error);
                setIsLoading(false);
            });
    }, [classId, DialogOpen, authState]);

    const renderContent = (): JSX.Element => {
        if (isLoading) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 4
                    }}
                >
                    <CircularProgress size={32}/>
                    <Typography>{t('common.loading')}</Typography>
                </Box>
            );
        }

        if (records.length === 0) {
            return (
                <Box
                    sx={{
                        textAlign: 'center',
                        py: 4
                    }}
                >
                    <Typography variant='body1'>{t('classAssessmentHistory.noHistory')}</Typography>
                </Box>
            );
        }

        return (
            <Box>
                {records.map((record, index) => (
                    <Box
                        key={`${record.assessmentId}-${index}`}
                        sx={{
                            mb: 3
                        }}
                    >
                        {
                            index > 0 && <Divider
                                sx={{
                                    mb: 3
                                }}
                            />
                        }
                        <Paper
                            sx={{
                                p: 2
                            }}
                            variant='outlined'
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 4,
                                    mb: 1.5
                                }}
                            >
                                <Typography variant='subtitle1'>
                                    <strong>{t('classAssessmentHistory.assessmentName')}</strong>{record.assessmentName}
                                </Typography>
                                <Typography variant='subtitle1'>
                                    <strong>{t('classAssessmentHistory.assessmentCode')}</strong>{record.assessmentId}
                                </Typography>
                            </Box>
                            <TableContainer>
                                <Table size='small'>
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                '& th': {
                                                    fontWeight: 'bold',
                                                    whiteSpace: 'nowrap'
                                                }
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    width: '40%'
                                                }}
                                            >
                                                {t('classAssessmentHistory.studentName')}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    width: '25%'
                                                }}
                                            >
                                                {t('classAssessmentHistory.studentId')}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    width: '35%'
                                                }}
                                            >
                                                {t('classAssessmentHistory.assignedDate')}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {record.enrolledStudents.length > 0 ? record.enrolledStudents.map((student) => (
                                            <TableRow key={student.studentId}>
                                                <TableCell
                                                    sx={{
                                                        width: '40%'
                                                    }}
                                                >
                                                    {student.studentName}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        width: '25%'
                                                    }}
                                                >
                                                    {student.studentId}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        width: '35%',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {student.submittedTimestamp ?? '-'}
                                                </TableCell>
                                            </TableRow>
                                        )) : (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={3}
                                                    sx={{
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    {t('classAssessmentHistory.noStudentData')}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <Dialog
            fullWidth
            maxWidth='md'
            open={DialogOpen}
        >
            <DialogTitle>{t('classAssessmentHistory.title')}</DialogTitle>
            <DialogContent>
                {renderContent()}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDialogClose}>
                    {t('classAssessmentHistory.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
